import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { SelectArrayInput, useChoices, useChoicesContext } from 'react-admin';

const defaultChoices = [];
const defaultOptions = {};
const defaultExcludeProductLines = [];

/**
 * Wrapper around SelectArrayInput that renders selected values as
 * comma-separated text instead of chips.
 */
const StyledSelectArrayInput = ({
    choices: choicesProp = defaultChoices,
    optionText = 'name',
    optionValue = 'id',
    translateChoice = true,
    options = defaultOptions,
    source: sourceProp,
    groupBy,
    excludeProductLines = defaultExcludeProductLines,
    sx,
    ...props
}) => {
    const choicesContext = useChoicesContext();
    const source = sourceProp ?? choicesContext?.source;

    const baseChoices = useMemo(() => (
        choicesProp.length > 0 ?
            choicesProp :
            choicesContext?.choices ??
            choicesContext?.availableChoices ??
            choicesContext?.allChoices ??
            []
    ), [choicesProp, choicesContext]);

    const processedChoices = useMemo(() => {
        if (!groupBy) return baseChoices;

        const ungroupedItems = baseChoices.filter((x) => !x[groupBy]);
        const groupedChoices = baseChoices.filter((x) => x[groupBy]);
        let groupedNames = [...new Set(groupedChoices.map((x) => x[groupBy]))];
        if (excludeProductLines.length > 0) {
            groupedNames = groupedNames.filter((x) => !excludeProductLines.includes(x));
        }

        return ungroupedItems.concat(groupedNames.map((gn) => {
            const _items = [{ [optionValue]: gn, [optionText]: gn, disabled: true }];
            const _choices = _items.concat(groupedChoices.filter((p) => p[groupBy] === gn));

            return _choices;
        }).flat());
    }, [baseChoices, groupBy, excludeProductLines, optionValue, optionText]);

    const { getChoiceText, getChoiceValue } = useChoices({
        optionText,
        optionValue,
        translateChoice,
    });

    const renderValue = useCallback(
        (selected) => {
            if (!selected?.length) return '';

            const labels = selected
                .map((value) => {
                    const choice = processedChoices.find((choiceItem) => (
                        String(getChoiceValue(choiceItem)) === String(value)
                    ));
                    return choice ? getChoiceText(choice) : value;
                })
                .filter(Boolean);

            return labels.join(', ');
        },
        [processedChoices, getChoiceText, getChoiceValue],
    );

    const mergedOptions = useMemo(
        () => ({
            ...options,
            renderValue: options.renderValue ?? renderValue,
        }),
        [options, renderValue],
    );

    return (
        <SelectArrayInput
            {...props}
            source={source}
            choices={processedChoices}
            optionText={optionText}
            optionValue={optionValue}
            translateChoice={translateChoice}
            options={mergedOptions}
            sx={{
                maxWidth: props.fullWidth ? '100%' : 240,
                '& .MuiSelect-select': {
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                },
                ...sx,
            }}
        />
    );
};

StyledSelectArrayInput.propTypes = {
    choices: PropTypes.arrayOf(PropTypes.object),
    groupBy: PropTypes.string,
    excludeProductLines: PropTypes.array,
    classes: PropTypes.object,
    className: PropTypes.string,
    children: PropTypes.node,
    label: PropTypes.string,
    options: PropTypes.object,
    optionText: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func,
        PropTypes.element,
    ]),
    optionValue: PropTypes.string,
    resource: PropTypes.string,
    source: PropTypes.string,
    translateChoice: PropTypes.bool,
};

export default React.memo(StyledSelectArrayInput);
